import {
  Drawer as MuiDrawer,
  Box,
  Typography,
  Fab,
  Modal,
  IconButton,
  Button,
} from "@mui/material";

import { styled, Theme, CSSObject } from "@mui/material/styles";
import FilterIcon from "../../assets/icons/filter.svg";
import OpenDrawerIcon from "../../assets/icons/openDrawer.svg";
import { ReactNode, useEffect, useRef, useState } from "react";
import classes from "./Sidebar.module.scss";
import useIsMobile from "../../hooks/useIsMobile.hook";
import { Close, FilterList as FilterListIcon } from "@mui/icons-material";
import { Stack } from "@mui/system";

interface SidebarDrawerProps {
  open: boolean;
  onToggle: () => void;
  showApplyFilters?: boolean;
  applyFilters?: () => void;
  children: ReactNode;
}

const drawerWidth = 288;
const MIN_WIDTH = 240;
const MAX_WIDTH = 560;
const WIDTH_STORAGE_KEY = "sidebarFilterWidth";

const readStoredWidth = (): number => {
  const saved = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
  return saved >= MIN_WIDTH && saved <= MAX_WIDTH ? saved : drawerWidth;
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  marginTop: "72px",
  zIndex: 7,
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  marginTop: "72px",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const SidebarDrawer: React.FC<SidebarDrawerProps> = ({
  open,
  onToggle,
  showApplyFilters,
  applyFilters,
  children,
}) => {
  const isMobile = useIsMobile();
  const [modalOpen, setModalOpen] = useState(false);
  const [width, setWidth] = useState<number>(readStoredWidth);
  const [isResizing, setIsResizing] = useState(false);
  const endResizeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
  }, [width]);

  // Guarantee the drag is torn down if the component unmounts mid-resize.
  useEffect(() => () => endResizeRef.current?.(), []);

  const startResize = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    setIsResizing(true);
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";

    const onMove = (moveEvent: MouseEvent) => {
      const next = startWidth + moveEvent.clientX - startX;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next)));
    };
    const endResize = () => {
      setIsResizing(false);
      document.body.style.userSelect = prevUserSelect;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", endResize);
      window.removeEventListener("blur", endResize);
      endResizeRef.current = null;
    };
    endResizeRef.current = endResize;
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", endResize);
    window.addEventListener("blur", endResize);
  };

  const handleModalApply = () => {
    setModalOpen(false);
    if (applyFilters) applyFilters();
  };

  if (isMobile) {
    return (
      <>
        <Fab
          data-testid="fab-button"
          className={classes["fab-button"]}
          onClick={() => setModalOpen(true)}
        >
          <FilterListIcon className={classes["icon"]} />
        </Fab>

        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box className={classes["filter-modal"]} data-testid="filter-modal">
            <Box className={classes["modal-header"]}>
              <Typography className={classes["modal-title"]}>
                Filters
              </Typography>
              <IconButton
                className={classes["close-button"]}
                onClick={() => setModalOpen(false)}
                data-testid="close-button"
              >
                <Close />
              </IconButton>
            </Box>
            <Box className={classes["modal-content"]}>{children}</Box>
            <Box className={classes["modal-footer"]}>
              <Button
                variant="contained"
                className={classes.applyBtn}
                onClick={handleModalApply}
                disabled={!showApplyFilters}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <Drawer
      data-testid="drawer"
      variant="permanent"
      open={open}
      sx={
        open
          ? {
              width,
              transition: isResizing ? "none" : undefined,
              "& .MuiDrawer-paper": {
                width,
                transition: isResizing ? "none" : undefined,
              },
            }
          : undefined
      }
    >
      <Box
        className={`${classes["drawer-container"]} ${
          open ? classes["expanded"] : ""
        }`}
      >
        <Box className={classes["drawer-header"]}>
          {open && (
            <Typography
              className={classes["drawer-header-text"]}
              data-testid="filter-text"
            >
              Filters
            </Typography>
          )}
          <IconButton
            className={classes["drawer-header-filter-icon"]}
            data-testid="filter-icon"
            onClick={onToggle}
            aria-label="Toggle filters"
          >
            <img src={FilterIcon} alt="" />
          </IconButton>
        </Box>
        <Stack className={classes.drawerBody}>
          <Box className={classes["drawer-content"]}>{children}</Box>
          <Box className={classes["drawer-footer"]}>
            {!open ? (
              <IconButton
                data-testid="open-drawer-icon"
                className={classes["drawer-footer-icon"]}
                onClick={onToggle}
                aria-label="Open filters"
              >
                <img src={OpenDrawerIcon} alt="" />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                className={classes.applyBtn}
                onClick={applyFilters}
                disabled={!showApplyFilters}
              >
                Apply
              </Button>
            )}
          </Box>
        </Stack>
      </Box>
      {open && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          className={classes["resize-handle"]}
          data-testid="resize-handle"
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize filters"
        />
      )}
    </Drawer>
  );
};

export default SidebarDrawer;
